import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import addDays from 'date-fns/addDays';
import parse from 'date-fns/parse';
import format from 'date-fns/format';
import { PAYMENT_STATUS, NI_ADDONS, PLANS } from 'products/nomad_insurance/constants';
import useIsUSPlanholder from 'products/nomad_insurance/hooks/useIsUSPlanholder';
import CaliforniaSpecialNotice from 'products/nomad_insurance/components/CaliforniaSpecialNotice';
import NewWhatsIncluded from 'products/nomad_insurance/components/NewWhatsIncluded';
import { setDestinations } from 'products/nomad_insurance/views/Purchase/steps/Destinations/state';
import usePolicyLink from 'products/nomad_insurance/hooks/usePolicyLink';
import { isBefore, startOfDay } from 'date-fns';
import USGroupMemberWarning from '../../components/USGroupMemberWarning';
import StepView from '../../components/StepView';
import MainContent from './components/MainContent';
import {
  addonChange,
  setIsUSPurchase,
  validateStep,
  setStartDate,
  setAddons
} from './state';
import { Column, DesktopTitle, MobileTitle, CoverageWrapper } from './styled';
import { useStepNavigationContext } from '../../context';
import { trackHubSpotEvent } from '../../utils/hubspot/hubspot-custom-events';
import { getAddonsFromCookie, setAddonCookie } from '../../utils/cookies';

const findAddon = (addonId) => {
  return Object.values(NI_ADDONS).find((addon) => {
    return addon.id === addonId;
  });
};

const Coverage = ({ className }) => {
  const dispatch = useDispatch();
  const { changeStep, isRenewal } = useStepNavigationContext();
  const [californiaSpecialNotice, setCaliforniaSpecialNotice] = useState(false);
  const [showUSGroupMemberWarning, setShowUSGroupMemberWarning] = useState(false);

  const { isHomeCountryUS, isCitizenshipUS } = useIsUSPlanholder();

  const { addons } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );
  const { destinations } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.destinations
  );
  const {
    isLoading: contractsLoading,
    activePaidContracts,
    lastCancelledContract: { contract: cancelledContract }
  } = useSelector((state) => state.nomadInsurance.contract);
  const { plans } = useSelector((state) => state.nomadInsurance.application);
  const policyLink = usePolicyLink(cancelledContract);
  const currentContract = activePaidContracts && activePaidContracts[0];
  const isRenewalContract =
    currentContract &&
    currentContract.paymentStatus === PAYMENT_STATUS.RENEWAL &&
    currentContract.endDate;

  const contractExists = currentContract && !contractsLoading;

  const USCoverageAddon = Object.values(NI_ADDONS).find(
    (addon) => addon.value === NI_ADDONS.USA_COVERAGE.value
  );
  const hasUSCoverage = addons.find((addon) => addon.id === USCoverageAddon.id);

  const { data: planholder } = useSelector((state) => state.app.profile);
  const { groupMembers } = useSelector((state) => state.nomadInsurance.application);

  const checkUSGroupMembers = (hasUSCoverage, groupMembers, callback) => {
    if (
      hasUSCoverage &&
      groupMembers.find(
        (groupMember) =>
          groupMember.citizenshipCountry === 'US' || groupMember.citizenship === 'US'
      )
    ) {
      setShowUSGroupMemberWarning(true);
      return;
    }
    callback();
  };

  const navigateToPurchase = useCallback(() => {
    dispatch(validateStep(true));

    void trackHubSpotEvent('coverage', planholder.email);

    changeStep(1);
  }, [planholder, dispatch, changeStep]);

  useEffect(() => {
    dispatch(setIsUSPurchase(isHomeCountryUS));
  }, [isHomeCountryUS]);

  useEffect(() => {
    const addonsFromCookie = getAddonsFromCookie();
    const mappedAddons = Object.values(NI_ADDONS).filter((addon) =>
      addonsFromCookie.includes(addon.value)
    );
    dispatch(setAddons(mappedAddons));
  }, []);

  useEffect(() => {
    setAddonCookie(addons.length > 0 ? addons.map((addon) => addon.value) : undefined);
  }, [addons]);

  useEffect(() => {
    if (contractExists && isRenewal && !isRenewalContract) {
      window.location.href = '/dashboard/nomad-insurance';
    }
  }, [isRenewal, isRenewalContract, contractExists]);

  useEffect(() => {
    if (!isRenewal || !isRenewalContract) {
      return;
    }

    // Prefill start date
    const startDate = addDays(
      parse(currentContract.renewalDate, 'yyyy-MM-dd', new Date()),
      1
    );
    const now = new Date();
    const minDate = startDate > now ? startDate : now;
    dispatch(setStartDate(minDate));

    // Prefill addons
    const currentAddons = currentContract.addonContracts;
    if (currentAddons) {
      const newAddons = currentAddons
        .map((addon) => {
          return findAddon(addon?.addonID);
        })
        .filter((addon) => !!addon);
      dispatch(setAddons(newAddons));
    }
  }, [isRenewal, currentContract, isRenewalContract]);

  useEffect(() => {
    // In case of citizenship == US --> remove only US_COVERAGE addon
    // TODO Await confirmation on US addon behaviour
    if (isCitizenshipUS || isHomeCountryUS) {
      addons
        .filter((addon) => addon.value === PLANS.USA_COVERAGE.value)
        .map((addon) => dispatch(addonChange(addon)));
    }
  }, [planholder?.homeCountry, addons]);

  useEffect(() => {
    if (!hasUSCoverage && destinations.includes('US')) {
      dispatch(setDestinations(destinations.filter((x) => x !== 'US')));
    }
  }, [hasUSCoverage, destinations]);

  const minStartDate = useMemo(() => {
    const renewalStartDate =
      isRenewal &&
      currentContract &&
      format(
        addDays(parse(currentContract.renewalDate, 'yyyy-MM-dd', new Date()), 1),
        'yyyy-MM-dd'
      );

    if (renewalStartDate) {
      return parse(renewalStartDate, 'yyyy-MM-dd', new Date());
    }
    if (!isRenewal && plans?.minStartDate) {
      return parse(plans.minStartDate, 'yyyy-MM-dd', new Date());
    }
    if (!isRenewal) {
      return new Date();
    }
  }, [plans, isRenewal, currentContract]);

  const minLocalStartDate = useMemo(() => {
    const localDate = startOfDay(new Date());
    return isBefore(minStartDate, localDate) ? localDate : minStartDate;
  }, [minStartDate]);

  return (
    <Column>
      <StepView>
        <CoverageWrapper className={className}>
          {showUSGroupMemberWarning && (
            <USGroupMemberWarning
              isOpen={!!showUSGroupMemberWarning}
              onClose={() => setShowUSGroupMemberWarning(false)}
              groupMembers={groupMembers}
              callback={navigateToPurchase}
            />
          )}

          {californiaSpecialNotice && (
            <CaliforniaSpecialNotice
              isOpen={californiaSpecialNotice}
              onClose={() => setCaliforniaSpecialNotice(false)}
              onYes={() => {
                dispatch(validateStep(true));
                void trackHubSpotEvent('coverage', planholder.email);
                changeStep(1);
              }}
              onNo={() => null}
            />
          )}

          <DesktopTitle>
            Hey {planholder?.firstName}, let's{' '}
            {isRenewal ? 'renew your insurance' : 'get you insured'}!
          </DesktopTitle>
          <MobileTitle>
            Let's {isRenewal ? 'renew your insurance' : 'get you insured'}!
          </MobileTitle>

          <MainContent
            minStartDate={minLocalStartDate}
            onNextClick={() =>
              checkUSGroupMembers(hasUSCoverage, groupMembers, navigateToPurchase)
            }
          />
        </CoverageWrapper>
      </StepView>
      <NewWhatsIncluded
        policyLink={policyLink}
        showReadFullPolicyButton={false}
        isAccordion
        variant="dark"
        isRenewal={isRenewal && isRenewalContract}
      />
    </Column>
  );
};

Coverage.propTypes = {
  className: PropTypes.string
};

export default Coverage;
